import React, { useState, useEffect, useRef, useCallback } from "react";

let useNavigate;
try { useNavigate = require("react-router-dom").useNavigate; }
catch { useNavigate = () => (path) => { window.location.href = path; }; }

let ameonLogo = null;
try { ameonLogo = require("../ameon.jpg"); } catch {}
const LOGO_SRC = ameonLogo || "/logo192.png";

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; -webkit-font-smoothing: antialiased; }
    body {
      font-family: 'DM Sans', system-ui, sans-serif;
      color: #1a1a2e;
      background: linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%);
      background-attachment: fixed;
      font-size: 15px;
      line-height: 1.6;
    }
    button { font-family: inherit; cursor: pointer; }
    input, select, textarea { font-family: inherit; font-size: 15px; }
    a { text-decoration: none; }

    :root {
      --blue: #4f8ef7;
      --blue-dark: #3a7ae8;
      --blue-light: #eff6ff;
      --blue-mid: #bfdbfe;
      --ink: #1a1a2e;
      --ink-muted: #555;
      --ink-faint: #888;
      --surface: #ffffff;
      --surface-2: rgba(255,255,255,0.65);
      --surface-3: rgba(248,250,252,0.8);
      --border: #f0f0f0;
      --border-strong: #e0e0e0;
      --green: #52c97e;
      --coral: #f97b6b;
      --purple: #a78bfa;
      --radius-sm: 8px;
      --radius-md: 12px;
      --radius-lg: 20px;
      --radius-xl: 28px;

      --text-xs: 12px;
      --text-sm: 13px;
      --text-base: 15px;
      --text-md: 16px;
      --text-lg: 18px;
      --text-xl: 20px;
      --text-2xl: 24px;
      --text-3xl: 30px;
      --text-hero: clamp(36px, 5.5vw, 62px);
      --text-section: clamp(26px, 4vw, 40px);
    }

    /* ── Animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulseDot {
      0%, 100% { box-shadow: 0 0 0 0 rgba(82,201,126,0.5); }
      50%       { box-shadow: 0 0 0 8px rgba(82,201,126,0); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-12px); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.94) translateY(20px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes navScan {
      0%   { left: -60%; opacity: 0; }
      10%  { opacity: 1; }
      90%  { opacity: 1; }
      100% { left: 120%; opacity: 0; }
    }

    .anim-fade-up  { animation: fadeUp 0.7s cubic-bezier(.22,.9,.36,1) both; }
    .anim-fade-in  { animation: fadeIn 0.6s ease both; }
    .anim-scale-in { opacity: 0; transform: scale(0.94) translateY(20px); }
    .anim-scale-in.visible { animation: scaleIn 0.6s cubic-bezier(.22,.9,.36,1) forwards; }

    /* ── Navbar ── */
    .navbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 9999;
      height: 68px;
      display: flex;
      align-items: center;
      background: transparent;
      backdrop-filter: none;
      border-bottom: 1px solid transparent;
      transition: all 0.3s;
      overflow: hidden;
    }
    .navbar.scrolled {
      background: rgba(255,255,255,0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom-color: var(--border);
      box-shadow: 0 1px 20px rgba(0,0,0,0.06);
    }
    .nav-inner {
      width: 100%; max-width: 1200px; margin: 0 auto;
      padding: 0 48px;
      display: flex; align-items: center; justify-content: space-between;
      position: relative; z-index: 2;
    }
    .nav-logo {
      display: flex; align-items: center; gap: 10px; cursor: pointer;
    }
    .nav-logo-img {
      width: 34px; height: 34px; border-radius: 8px; object-fit: contain;
      border: 1px solid var(--border);
    }
    .nav-logo-text { display: flex; flex-direction: column; line-height: 1; }
    .nav-logo-name {
      font-family: 'Playfair Display', serif;
      font-weight: 700; font-size: 22px; color: var(--ink); letter-spacing: -0.3px;
    }
    .nav-logo-name span { color: var(--blue); }
    .nav-logo-sub {
      font-size: var(--text-xs); font-weight: 600; letter-spacing: 1.4px;
      color: var(--blue); text-transform: uppercase; margin-top: 1px;
    }
    .nav-links {
      display: flex; align-items: center; gap: 32px;
    }
    .nav-link {
      padding: 6px 0; border: none;
      background: transparent; color: var(--ink-muted); font-size: 14px;
      font-weight: 500; cursor: pointer;
      transition: color 0.2s;
      font-family: 'DM Sans', sans-serif;
      text-decoration: none;
    }
    .nav-link:hover, .nav-link.active { color: var(--blue); }
    .nav-actions { display: flex; align-items: center; gap: 8px; }
    .btn-ghost-sm {
      padding: 8px 22px; border-radius: 50px;
      border: 1.5px solid var(--blue);
      background: transparent; color: var(--blue); font-size: 14px; font-weight: 500;
      cursor: pointer; transition: background 0.2s, color 0.2s;
      font-family: 'DM Sans', sans-serif;
    }
    .btn-ghost-sm:hover { background: var(--blue); color: white; }
    .btn-blue-sm {
      padding: 8px 22px; border-radius: 50px;
      border: none; background: linear-gradient(135deg, var(--blue), #6366f1); color: #fff;
      font-size: 14px; font-weight: 500; cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      box-shadow: 0 4px 20px rgba(79,142,247,0.35);
      font-family: 'DM Sans', sans-serif;
    }
    .btn-blue-sm:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(79,142,247,0.45); }

    /* Mobile hamburger */
    .mobile-menu-btn {
      display: none; background: none; border: none;
      cursor: pointer; padding: 6px; flex-direction: column; gap: 5px;
    }
    .hamburger-line {
      display: block; width: 22px; height: 2px;
      background: var(--ink); border-radius: 2px; transition: 0.3s;
    }
    .mobile-nav {
      display: none;
      position: fixed; top: 68px; left: 0; right: 0; z-index: 9998;
      background: rgba(255,255,255,0.97); backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--border);
      padding: 12px 16px 16px;
    }
    .mobile-nav.open { display: block; animation: fadeIn 0.2s ease; }
    .mobile-nav-link {
      display: block; width: 100%; text-align: left;
      padding: 11px 14px; border: none; background: none;
      color: var(--ink); font-size: var(--text-base); font-weight: 500;
      cursor: pointer; border-radius: var(--radius-sm);
      transition: background 0.15s; font-family: 'DM Sans', sans-serif;
    }
    .mobile-nav-link:hover { background: var(--surface-3); }
    .mobile-nav-divider { height: 1px; background: var(--border); margin: 10px 0; }

    /* ── Buttons ── */
    .btn-primary {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 14px 32px; border-radius: 50px;
      border: none;
      background: linear-gradient(135deg, var(--blue), #6366f1);
      color: #fff;
      font-size: var(--text-base); font-weight: 500; cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      box-shadow: 0 4px 20px rgba(79,142,247,0.35);
      font-family: 'DM Sans', sans-serif;
    }
    .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(79,142,247,0.45); }

    .btn-secondary {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 12px 30px; border-radius: 50px;
      border: 2px solid var(--coral); background: transparent;
      color: var(--coral); font-size: var(--text-base); font-weight: 500; cursor: pointer;
      transition: background 0.2s, color 0.2s;
      font-family: 'DM Sans', sans-serif;
    }
    .btn-secondary:hover { background: var(--coral); color: white; }

    .btn-blue {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 13px 28px; border-radius: 50px;
      border: none;
      background: linear-gradient(135deg, var(--blue), #6366f1);
      color: #fff;
      font-size: var(--text-base); font-weight: 500; cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      box-shadow: 0 4px 20px rgba(79,142,247,0.35);
      font-family: 'DM Sans', sans-serif;
    }
    .btn-blue:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(79,142,247,0.45); }
    .btn-blue:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }

    /* ── Section Helpers ── */
    .section-wrap { max-width: 1200px; margin: 0 auto; padding: 0 48px; }

    .eyebrow {
      display: inline-flex; align-items: center; gap: 6px;
      font-size: var(--text-xs); font-weight: 600; letter-spacing: 0.1em;
      text-transform: uppercase; color: var(--blue); margin-bottom: 12px;
      font-family: 'DM Sans', sans-serif;
    }
    .eyebrow-dot {
      width: 8px; height: 8px; border-radius: 50%; background: var(--green);
      animation: pulseDot 2s ease-in-out infinite;
    }

    .section-heading {
      font-family: 'Playfair Display', serif;
      font-size: var(--text-section); font-weight: 800;
      color: var(--ink); letter-spacing: -0.5px; line-height: 1.15;
      margin-bottom: 12px;
    }

    .section-sub {
      font-size: var(--text-base); color: var(--ink-muted); line-height: 1.75;
      max-width: 520px; margin: 0 auto 48px;
      font-family: 'DM Sans', sans-serif;
    }
    .section-sub.left { margin: 0 0 48px; }

    /* ── Cards ── */
    .card {
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg); padding: 32px 28px;
      transition: box-shadow 0.3s, transform 0.3s;
      box-shadow: 0 2px 20px rgba(0,0,0,0.06);
    }
    .card:hover {
      box-shadow: 0 12px 40px rgba(0,0,0,0.1);
      transform: translateY(-6px);
    }
    .card-icon {
      width: 52px; height: 52px; border-radius: 14px;
      display: flex; align-items: center;
      justify-content: center; margin-bottom: 18px; flex-shrink: 0;
      font-size: 24px;
    }

    /* ── Check items ── */
    .check-item {
      display: flex; align-items: flex-start; gap: 9px;
      font-size: var(--text-sm); color: var(--ink-muted); margin-bottom: 9px; line-height: 1.6;
      font-family: 'DM Sans', sans-serif;
    }
    .check-icon { color: var(--blue); font-size: var(--text-base); margin-top: 1px; flex-shrink: 0; }
    .check-icon-green { color: var(--green); font-size: var(--text-base); margin-top: 1px; flex-shrink: 0; }

    /* ── Pricing cards ── */
    .plan-card {
      background: white;
      border: 2px solid var(--border);
      border-radius: var(--radius-xl); overflow: hidden;
      display: flex; flex-direction: column;
      transition: box-shadow 0.3s, transform 0.3s;
      position: relative;
    }
    .plan-card:hover { box-shadow: 0 20px 50px rgba(0,0,0,0.1); transform: translateY(-8px); }
    .plan-card.featured { border-color: var(--coral); transform: scale(1.03); box-shadow: 0 8px 32px rgba(249,123,107,0.2); }
    .plan-card.featured:hover { transform: scale(1.03) translateY(-8px); }

    /* ── FAQ ── */
    .faq-card {
      background: white;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg); padding: 28px 28px;
      transition: box-shadow 0.2s, transform 0.2s;
      box-shadow: 0 2px 12px rgba(0,0,0,0.04);
    }
    .faq-card:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.08); transform: translateY(-3px); }

    /* ── Hero blob ── */
    .hero-blob {
      position: absolute; border-radius: 50%;
      filter: blur(80px); opacity: 0.18; pointer-events: none;
    }

    /* ── Responsive ── */
    @media (max-width: 900px) {
      .nav-links, .nav-actions { display: none; }
      .mobile-menu-btn { display: flex; }
      .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
      .contact-grid { grid-template-columns: 1fr !important; }
      .plans-grid { grid-template-columns: 1fr 1fr !important; }
      .nav-inner { padding: 0 24px; }
    }
    @media (max-width: 640px) {
      .features-grid   { grid-template-columns: 1fr !important; }
      .payroll-grid    { grid-template-columns: 1fr !important; }
      .plans-grid      { grid-template-columns: 1fr !important; }
      .stats-grid      { grid-template-columns: 1fr 1fr !important; }
      .who-grid        { grid-template-columns: 1fr 1fr !important; }
      .footer-grid     { grid-template-columns: 1fr !important; }
      .hero-btns       { flex-direction: column !important; align-items: stretch !important; }
      .cta-btns        { flex-direction: column !important; align-items: center !important; }
      .steps-grid      { grid-template-columns: 1fr 1fr !important; }
      .profession-grid { grid-template-columns: 1fr 1fr !important; }
      .industry-grid   { grid-template-columns: 1fr 1fr 1fr !important; }
      .why-grid        { grid-template-columns: 1fr !important; }
      .section-wrap    { padding: 0 20px; }
    }
    @media (max-width: 400px) {
      .industry-grid { grid-template-columns: 1fr 1fr !important; }
    }

    /* ── Input focus ── */
    input:focus, select:focus, textarea:focus {
      outline: none;
      border-color: var(--blue) !important;
      box-shadow: 0 0 0 3px rgba(79,142,247,.12);
    }

    /* ── Footer links ── */
    .footer-link { transition: color 0.2s; }
    .footer-link:hover { color: var(--blue) !important; }
    .policy-link:hover { color: var(--blue) !important; }

    /* ── Pulse dot ── */
    .pulse-dot {
      width: 8px; height: 8px; background: var(--green);
      border-radius: 50%; display: inline-block;
      animation: pulseDot 2s infinite;
    }

    /* ── Floating hero card ── */
    .floating { animation: float 4s ease-in-out infinite; }
  `}</style>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <div className="eyebrow">
    <span className="eyebrow-dot" />
    {children}
  </div>
);

const SectionHeading = ({ children, style = {} }) => (
  <h2 className="section-heading" style={style}>{children}</h2>
);

const SectionSub = ({ children, left }) => (
  <p className={`section-sub${left ? " left" : ""}`}>{children}</p>
);

const CheckItem = ({ children, green }) => (
  <div className="check-item">
    <span className={green ? "check-icon-green" : "check-icon"}>✓</span>
    <span>{children}</span>
  </div>
);

function useScrollAnim() {
  const refs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    refs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const setRef = useCallback((i) => (el) => { refs.current[i] = el; }, []);
  return setRef;
}

// ─── POLICY PAGES ─────────────────────────────────────────────────────────────
const POLICY_META = {
  privacy:  { title: "Privacy Policy",              subtitle: "How we collect, use, and protect your data",  badge: "Last updated: January 2026" },
  terms:    { title: "Terms & Conditions",           subtitle: "Rules and guidelines for using InvoiceCloud", badge: "Effective: January 2026" },
  refund:   { title: "Refund & Cancellation Policy", subtitle: "Our fair refund and cancellation process",    badge: "Last updated: January 2026" },
  shipping: { title: "Delivery Policy",              subtitle: "How we deliver our 100% digital services",    badge: "Digital delivery only" },
  contact:  { title: "Contact Us",                   subtitle: "We'd love to hear from you",                  badge: "We respond within 24 hours" },
};

function PolicyPage({ type, onClose, onOpenPolicy, navigate }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [scrolled, setScrolled] = useState(false);
  const meta = POLICY_META[type];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const InfoCard = ({ children, style = {} }) => (
    <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "24px 22px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", ...style }}>{children}</div>
  );

  const content = {
    privacy: (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <p style={{ color: "var(--ink-muted)", lineHeight: 1.8, fontSize: "var(--text-base)", fontFamily: "'DM Sans', sans-serif" }}>
          We value your privacy and are committed to protecting your personal information. This policy explains how{" "}
          <strong style={{ color: "var(--ink)" }}>InvoiceCloud Canada Inc.</strong> collects and uses your data in accordance with{" "}
          <strong style={{ color: "var(--blue)" }}>PIPEDA</strong>.
        </p>
        <InfoCard>
          <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 14, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>1. Information We Collect</h3>
          {["Full name", "Email address", "Phone number", "Billing & payment information", "Payment details — processed securely via Stripe"].map((item, i) => (
            <CheckItem key={i}>{item}</CheckItem>
          ))}
        </InfoCard>
        <InfoCard>
          <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 14, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>2. How We Use Your Information</h3>
          {["To provide and improve our billing and payroll services", "To process payments securely via Stripe", "To communicate regarding services or support", "To comply with Canadian legal obligations under PIPEDA"].map((item, i) => (
            <CheckItem key={i}>{item}</CheckItem>
          ))}
        </InfoCard>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>3. Payment Security</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>All transactions processed through <strong>Stripe</strong>. We never store card details.</p>
          </InfoCard>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>4. Data Retention</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>Retained only as long as needed or required by Canadian law.</p>
          </InfoCard>
        </div>
        <InfoCard style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)" }}>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", fontFamily: "'DM Sans', sans-serif" }}>Privacy questions? Email <a href="mailto:billing@invoicecloud.ca" style={{ color: "var(--blue)", fontWeight: 700 }}>billing@invoicecloud.ca</a></p>
        </InfoCard>
      </div>
    ),
    terms: (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <p style={{ color: "var(--ink-muted)", lineHeight: 1.8, fontSize: "var(--text-base)", fontFamily: "'DM Sans', sans-serif" }}>By using InvoiceCloud Canada, you agree to these terms governed by the laws of <strong style={{ color: "var(--blue)" }}>Ontario, Canada</strong>.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>Our Services</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>Cloud-based GST/HST/PST billing, payroll management, and CRA-compliant reporting.</p>
          </InfoCard>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>Payments</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>All payments via <strong>Stripe</strong>. Subscriptions auto-renew unless cancelled before renewal.</p>
          </InfoCard>
        </div>
        <InfoCard>
          <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>Intellectual Property</h3>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>All content belongs to <strong>InvoiceCloud Canada Inc.</strong> Unauthorized use is prohibited.</p>
        </InfoCard>
        <InfoCard>
          <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>Limitation of Liability</h3>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>InvoiceCloud is provided "as is." We are not liable for indirect or consequential damages.</p>
        </InfoCard>
        <InfoCard style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)" }}>
          <p style={{ fontSize: "var(--text-sm)", fontFamily: "'DM Sans', sans-serif" }}>Questions? <a href="mailto:billing@invoicecloud.ca" style={{ color: "var(--blue)", fontWeight: 700 }}>billing@invoicecloud.ca</a></p>
        </InfoCard>
      </div>
    ),
    refund: (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <InfoCard style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
          <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>14-Day Free Trial — No Risk</h3>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>All plans include a <strong>14-day free trial</strong>. No payment charged. Cancel anytime.</p>
        </InfoCard>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>Refund Eligibility</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>Refunds considered if service not yet initiated. Processed within 7–10 business days.</p>
          </InfoCard>
          <InfoCard>
            <h3 style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8, fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>Cancellation</h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>Cancel anytime from account settings. Effective at end of billing cycle.</p>
          </InfoCard>
        </div>
        <InfoCard style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)" }}>
          <p style={{ fontSize: "var(--text-sm)", fontFamily: "'DM Sans', sans-serif" }}>Request a refund: <a href="mailto:billing@invoicecloud.ca" style={{ color: "var(--blue)", fontWeight: 700 }}>billing@invoicecloud.ca</a></p>
        </InfoCard>
      </div>
    ),
    shipping: (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <InfoCard style={{ background: "var(--ink)", textAlign: "center" }}>
          <h3 style={{ fontSize: "var(--text-2xl)", fontWeight: 800, marginBottom: 8, color: "#fff", fontFamily: "'Playfair Display', serif" }}>100% Digital Service</h3>
          <p style={{ color: "rgba(255,255,255,.7)", fontSize: "var(--text-sm)", lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif" }}>InvoiceCloud Canada is entirely cloud-based. There is <strong style={{ color: "#fff" }}>no physical product or shipping</strong>.</p>
        </InfoCard>
        {[
          { t: "Instant Access",  d: "Account access granted immediately upon registration" },
          { t: "Full Features",   d: "All features available instantly after subscription activation" },
          { t: "Auto Updates",    d: "Software updates delivered automatically via the cloud" },
          { t: "Support",         d: "Email and phone support during business hours" },
        ].map((item, i) => (
          <InfoCard key={i}>
            <strong style={{ color: "var(--ink)", fontSize: "var(--text-base)", fontFamily: "'Playfair Display', serif" }}>{item.t}</strong>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{item.d}</p>
          </InfoCard>
        ))}
      </div>
    ),
    contact: (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <a href="mailto:billing@invoicecloud.ca" style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)", borderRadius: "var(--radius-lg)", padding: "22px 18px", display: "block" }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-faint)", fontWeight: 700, letterSpacing: "1px", marginBottom: 5, fontFamily: "'DM Sans', sans-serif" }}>EMAIL</div>
            <div style={{ fontWeight: 700, color: "var(--ink)", fontSize: "var(--text-base)", fontFamily: "'DM Sans', sans-serif" }}>billing@invoicecloud.ca</div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-muted)", marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>We respond within 24 hours</div>
          </a>
          <a href="tel:+14160000000" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "var(--radius-lg)", padding: "22px 18px", display: "block" }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-faint)", fontWeight: 700, letterSpacing: "1px", marginBottom: 5, fontFamily: "'DM Sans', sans-serif" }}>PHONE</div>
            <div style={{ fontWeight: 700, color: "var(--ink)", fontSize: "var(--text-base)", fontFamily: "'DM Sans', sans-serif" }}>+1 (416) 000-0000</div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-muted)", marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>Mon–Fri, 9AM–6PM EST</div>
          </a>
        </div>
        <InfoCard>
          <div style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 4, fontSize: "var(--text-base)", fontFamily: "'DM Sans', sans-serif" }}>InvoiceCloud Canada Inc.</div>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--ink-muted)", fontFamily: "'DM Sans', sans-serif" }}>Toronto, Ontario, Canada</div>
        </InfoCard>
        <InfoCard style={{ background: "var(--ink)" }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16, color: "#fff", fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>Business Hours</h3>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "var(--text-sm)", fontFamily: "'DM Sans', sans-serif" }}>
            <span style={{ color: "rgba(255,255,255,.6)" }}>Monday – Friday</span>
            <span style={{ fontWeight: 700, color: "#fff" }}>9:00 AM – 6:00 PM EST</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-sm)", fontFamily: "'DM Sans', sans-serif" }}>
            <span style={{ color: "rgba(255,255,255,.6)" }}>Saturday – Sunday</span>
            <span style={{ color: "rgba(255,255,255,.35)" }}>Closed</span>
          </div>
        </InfoCard>
      </div>
    ),
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%)", backgroundAttachment: "fixed" }}>
      <GlobalStyles />
      <header className={`navbar${scrolled ? " scrolled" : ""}`}>
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => { onClose(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <img src={LOGO_SRC} alt="InvoiceCloud" className="nav-logo-img" onError={e => e.currentTarget.style.display = "none"} />
            <div className="nav-logo-text">
              <span className="nav-logo-name">Invoice<span>Cloud</span></span>
              <span className="nav-logo-sub">Canada</span>
            </div>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost-sm" onClick={() => navigate("/login")}>Login</button>
            <button className="btn-blue-sm" onClick={() => navigate("/login")}>Free Trial</button>
          </div>
        </div>
      </header>

      <div style={{ paddingTop: 68 }}>
        <div style={{ background: "rgba(255,255,255,0.75)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--border)", padding: "56px 24px 48px", textAlign: "center" }}>
          <span style={{ display: "inline-block", background: "var(--blue-light)", color: "var(--blue)", borderRadius: 999, padding: "4px 14px", fontSize: "var(--text-xs)", fontWeight: 600, marginBottom: 18, letterSpacing: "0.06em", fontFamily: "'DM Sans', sans-serif" }}>{meta.badge}</span>
          <h1 style={{ fontSize: "clamp(26px,4vw,38px)", fontWeight: 800, color: "var(--ink)", marginBottom: 10, letterSpacing: "-0.5px", fontFamily: "'Playfair Display', serif" }}>{meta.title}</h1>
          <p style={{ color: "var(--ink-muted)", fontSize: "var(--text-md)", maxWidth: 480, margin: "0 auto", fontFamily: "'DM Sans', sans-serif" }}>{meta.subtitle}</p>
        </div>
        <div style={{ background: "rgba(241,245,249,0.7)", backdropFilter: "blur(8px)", borderBottom: "1px solid var(--border)", padding: "10px 24px" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", gap: 8, fontSize: "var(--text-sm)", color: "var(--ink-faint)", fontFamily: "'DM Sans', sans-serif" }}>
            <button onClick={() => { onClose(); window.scrollTo(0, 0); }} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontWeight: 700, padding: 0 }}>Home</button>
            <span>›</span>
            <span style={{ color: "var(--ink-muted)", fontWeight: 600 }}>{meta.title}</span>
          </div>
        </div>
        <main style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px" }}>
          {content[type]}
          <div style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--border)", textAlign: "center" }}>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--ink-faint)", fontFamily: "'DM Sans', sans-serif" }}>© 2026 InvoiceCloud Canada Inc. Made in Canada 🍁</p>
          </div>
        </main>
      </div>

      <footer style={{ background: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)", borderTop: "1px solid var(--border)", padding: "16px 24px", textAlign: "center" }}>
        {[
          { label: "Privacy Policy", key: "privacy" }, { label: "Terms & Conditions", key: "terms" },
          { label: "Refund Policy",  key: "refund"  }, { label: "Delivery Policy",    key: "shipping" },
          { label: "Contact",        key: "contact" },
        ].map((link, i, arr) => (
          <React.Fragment key={link.key}>
            <button className="policy-link" onClick={() => { onOpenPolicy(link.key); window.scrollTo(0, 0); }}
              style={{ background: "none", border: "none", color: "var(--ink-faint)", fontSize: "var(--text-sm)", cursor: "pointer", padding: "4px 6px", transition: "color .2s", fontFamily: "'DM Sans', sans-serif" }}>
              {link.label}
            </button>
            {i < arr.length - 1 && <span style={{ color: "var(--border)", fontSize: "var(--text-xs)" }}>|</span>}
          </React.Fragment>
        ))}
      </footer>
    </div>
  );
}

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "features", label: "Features" },
  { id: "payroll",  label: "Payroll"  },
  { id: "pricing",  label: "Pricing"  },
  { id: "who-uses", label: "Who Uses" },
  { id: "why-us",   label: "Why Us"   },
  { id: "contact",  label: "Contact"  },
];

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function FirstScreen() {
  const navigate = useNavigate();

  const [billing,          setBilling]          = useState("monthly");
  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [activeSection,    setActiveSection]    = useState("");
  const [scrolled,         setScrolled]         = useState(false);
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [submitStatus,     setSubmitStatus]     = useState({ type: "", message: "" });
  const [activePolicyPage, setActivePolicyPage] = useState(null);

  const pricing = {
    monthly: { basic: 9, professional: 19, business: 39, enterprise: 99 },
    yearly:  { basic: 89, professional: 189, business: 389, enterprise: 990 },
  };

  const planColors = {
    basic:        { color: "#4f8ef7", bg: "#eff6ff" },
    professional: { color: "#f97b6b", bg: "#fff5f4" },
    business:     { color: "#a78bfa", bg: "#f5f3ff" },
    enterprise:   { color: "#52c97e", bg: "#f0fdf4" },
  };

  const openPolicy  = (key) => { window.history.pushState({ policy: key }, "", window.location.pathname); setActivePolicyPage(key); window.scrollTo(0, 0); };
  const closePolicy = () => { setActivePolicyPage(null); window.scrollTo(0, 0); };

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const ids = NAV_ITEMS.map(n => n.id);
      const cur = ids.find(id => {
        const el = document.getElementById(id);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= 100 && r.bottom >= 100;
      });
      if (cur) setActiveSection(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const fn = () => { if (activePolicyPage) { setActivePolicyPage(null); window.scrollTo(0, 0); } };
    window.addEventListener("popstate", fn);
    return () => window.removeEventListener("popstate", fn);
  }, [activePolicyPage]);

  useEffect(() => {
    if (submitStatus.message) { const t = setTimeout(() => setSubmitStatus({ type: "", message: "" }), 5000); return () => clearTimeout(t); }
  }, [submitStatus]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(id);
    setMobileOpen(false);
  };
  const handleNavClick = (id) => {
    if (!id) { window.scrollTo({ top: 0, behavior: "smooth" }); setMobileOpen(false); return; }
    scrollTo(id);
  };

  // ✅ UPDATED: API endpoint changed to localhost:5002
  const submitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const fd = new FormData(e.target);
    const data = {
      name: fd.get("name"), email: fd.get("email"), phone: fd.get("phone"),
      plan: fd.get("plan"), message: fd.get("message"),
      date: new Date().toLocaleString("en-CA", { timeZone: "America/Toronto" }),
      source: "InvoiceCloud Canada Contact Form",
    };
    try {
      const res = await fetch("https://canada-billing.ameontech.com/api/contact-form", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      const r = await res.json();
      if (r.success) { setSubmitStatus({ type: "success", message: `Thank you ${data.name}! We'll contact you within 24 hours.` }); e.target.reset(); }
      else throw new Error();
    } catch {
      setSubmitStatus({ type: "error", message: "Sorry, there was an error. Please email billing@invoicecloud.ca directly." });
    } finally { setIsSubmitting(false); }
  };

  const setRef = useScrollAnim();

  if (activePolicyPage) {
    return (
      <PolicyPage
        type={activePolicyPage}
        onClose={closePolicy}
        navigate={navigate}
        onOpenPolicy={(k) => { window.history.pushState({ policy: k }, "", window.location.pathname); setActivePolicyPage(k); window.scrollTo(0, 0); }}
      />
    );
  }

  const legalLinks = [
    { label: "Privacy Policy", key: "privacy" }, { label: "Terms & Conditions", key: "terms" },
    { label: "Refund Policy",  key: "refund"  }, { label: "Delivery Policy",    key: "shipping" },
    { label: "Contact",        key: "contact" },
  ];

  const plans = [
    { name: "Basic",         key: "basic",        target: "Freelancers",      popular: false, cta: "Start Free Trial",
      features: ["Up to 50 invoices/month", "GST/HST invoice generation", "Basic dashboard", "Email support"] },
    { name: "Professional",  key: "professional", target: "Small Businesses", popular: true,  cta: "Start Free Trial",
      features: ["Unlimited invoices", "GST/HST/PST billing", "Expense management", "Stripe payments", "T1/T2 CRA ready reports"] },
    { name: "Business Pro+", key: "business",     target: "SMEs & Agencies",  popular: false, cta: "Start Free Trial",
      features: ["Multiple businesses", "Advanced GST/HST reports", "T2 JSON generation", "Role-based access", "Priority support"] },
    { name: "Enterprise",    key: "enterprise",   target: "CPAs & Firms",     popular: false, cta: "Contact Sales",
      features: ["Multiple client accounts", "Dedicated dashboard", "Bulk T-slip processing", "Custom integrations", "Dedicated account manager"] },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "transparent" }}>
      <GlobalStyles />

      {/* ═══ STICKY NAVBAR ═══════════════════════════════════════════════════ */}
      <header className={`navbar${scrolled ? " scrolled" : ""}`}>
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => handleNavClick(null)}>
            <img src={LOGO_SRC} alt="InvoiceCloud" className="nav-logo-img" onError={e => e.currentTarget.style.display = "none"} />
            <div className="nav-logo-text">
              <span className="nav-logo-name">Invoice<span>Cloud</span></span>
              <span className="nav-logo-sub">Canada</span>
            </div>
          </div>
          <nav className="nav-links">
            {NAV_ITEMS.map(item => (
              <button key={item.id} className={`nav-link${activeSection === item.id ? " active" : ""}`}
                onClick={() => handleNavClick(item.id)}>
                {item.label}
              </button>
            ))}
          </nav>
          <div className="nav-actions">
            <button className="btn-ghost-sm" onClick={() => navigate("/login")}>Login</button>
            <button className="btn-blue-sm" onClick={() => navigate("/login")}>Start Free Trial</button>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </header>

      {/* Mobile nav */}
      <div className={`mobile-nav${mobileOpen ? " open" : ""}`}>
        {NAV_ITEMS.map(item => (
          <button key={item.id} className="mobile-nav-link" onClick={() => handleNavClick(item.id)}>
            {item.label}
          </button>
        ))}
        <div className="mobile-nav-divider" />
        <button className="mobile-nav-link" onClick={() => navigate("/login")}>Login</button>
        <button style={{ width: "100%", margin: "6px 0 0" }} className="btn-blue" onClick={() => navigate("/login")}>
          Start Free Trial
        </button>
      </div>

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <section style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        background: "linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%)",
        position: "relative", overflow: "hidden",
        paddingTop: 68,
      }}>
        <div className="hero-blob" style={{ width: 500, height: 500, background: "#4f8ef7", top: -100, right: -100 }} />
        <div className="hero-blob" style={{ width: 400, height: 400, background: "#f97b6b", bottom: -80, left: -80 }} />
        <div className="hero-blob" style={{ width: 300, height: 300, background: "#a78bfa", top: "40%", left: "40%" }} />

        <div className="section-wrap" style={{ width: "100%", paddingTop: 60, paddingBottom: 60 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 60, maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ flex: 1 }}>
              <div className="anim-fade-up" style={{ animationDelay: "0s", marginBottom: 28 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "white", padding: "6px 16px", borderRadius: 50, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                  <span className="pulse-dot" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#555" }}>GST/HST billing for Canadian businesses</span>
                </div>
              </div>

              <h1 className="anim-fade-up" style={{
                animationDelay: "0.1s",
                fontFamily: "'Playfair Display', serif",
                fontSize: "var(--text-hero)",
                fontWeight: 800, lineHeight: 1.1, color: "var(--ink)", marginBottom: 20,
                letterSpacing: "-1px",
              }}>
                Professional<br />
                <span style={{ color: "#4f8ef7" }}>Billing & Payroll</span><br />
                for Canada
              </h1>

              <p className="anim-fade-up" style={{
                animationDelay: "0.18s",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 18, color: "#666", lineHeight: 1.7, marginBottom: 36, maxWidth: 480,
              }}>
                Complete GST/HST/PST billing, payroll management, and CRA-compliant
                reporting — designed for Canadian businesses and CPAs.
              </p>

              <div className="hero-btns anim-fade-up" style={{
                animationDelay: "0.25s",
                display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 40,
              }}>
                <button className="btn-primary" onClick={() => navigate("/login")}>
                  Start 14-day free trial →
                </button>
                <button className="btn-secondary"
                  onClick={() => alert("Demo Request Received!\nOur team will contact you to schedule a demo.")}>
                  Request demo
                </button>
              </div>

              <div className="anim-fade-up" style={{ animationDelay: "0.32s", display: "flex", gap: 32, flexWrap: "wrap" }}>
                {[["5 min", "Setup time"], ["CRA ✓", "Always compliant"], ["T4/CPP", "Payroll built-in"]].map(([val, label]) => (
                  <div key={label}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "var(--ink)" }}>{val}</div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888" }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="floating" style={{ flex: 1, position: "relative", display: "flex", justifyContent: "center" }}>
              <div style={{ background: "white", borderRadius: 28, padding: 32, boxShadow: "0 20px 60px rgba(0,0,0,0.12)", maxWidth: 420, width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #4f8ef7, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🍁</div>
                  <div>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "var(--ink)" }}>InvoiceCloud Canada</div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#888" }}>Toronto, Ontario • GST/HST Ready</div>
                  </div>
                  <span style={{ marginLeft: "auto", background: "#e8f5e9", color: "#52c97e", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>Active</span>
                </div>

                {[
                  { label: "Total Invoiced",    value: "$124,000", color: "#4f8ef7", pct: 72 },
                  { label: "GST/HST Collected", value: "$16,120",  color: "#f97b6b", pct: 55 },
                  { label: "Payroll Processed", value: "$86,500",  color: "#52c97e", pct: 88 },
                ].map(({ label, value, color, pct }) => (
                  <div key={label} style={{ marginBottom: 18 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666" }}>{label}</span>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{value}</span>
                    </div>
                    <div style={{ background: "#f0f0f0", borderRadius: 8, height: 7, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 8 }} />
                    </div>
                  </div>
                ))}

                <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                  <button style={{ flex: 1, padding: "10px", background: "linear-gradient(135deg, #4f8ef7, #6366f1)", color: "white", border: "none", borderRadius: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: "pointer" }}
                    onClick={() => navigate("/login")}>Create Invoice</button>
                  <button style={{ flex: 1, padding: "10px", background: "#fff5f4", color: "#f97b6b", border: "1px solid #fde8e5", borderRadius: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: "pointer" }}
                    onClick={() => navigate("/login")}>Run Payroll</button>
                </div>
              </div>

              <div style={{ position: "absolute", top: -20, right: -20, background: "white", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>📋</span>
                <div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#888" }}>CRA Report Ready</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>T4 Slips 2025</div>
                </div>
              </div>

              <div style={{ position: "absolute", bottom: -10, left: -20, background: "white", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#888" }}>Invoice Paid</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: "#52c97e" }}>$4,200 today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══════════════════════════════════════════════════════ */}
      <section style={{ background: "white", padding: "60px 48px", borderBottom: "1px solid var(--border)" }}>
        <div className="stats-grid section-wrap" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
          {[
            { val: "5 min",    label: "Setup time",         sub: "Ready to bill fast" },
            { val: "CRA ✓",    label: "Always compliant",   sub: "Auto tax calculations" },
            { val: "T4 / CPP", label: "Payroll built-in",   sub: "EI, T4 slips included" },
            { val: "256-bit",  label: "Bank-level security", sub: "Your data protected" },
          ].map((item, i) => (
            <div key={i} style={{ padding: "24px 20px", textAlign: "center", borderRight: i < 3 ? "1px solid var(--border)" : "none" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, fontWeight: 800, color: "var(--ink)", marginBottom: 4 }}>{item.val}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888" }}>{item.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ════════════════════════════════════════════════════════ */}
      <section id="features" style={{ padding: "88px 0", background: "#fafafa" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow>Features</Eyebrow>
            <SectionHeading>Everything you need to bill<br />professionally</SectionHeading>
            <SectionSub>Complete solution designed specifically for Canadian businesses and CPAs.</SectionSub>
          </div>

          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {[
              { icon: "🧾", title: "GST/HST/PST Invoicing",     desc: "Auto tax calculations, CRA-compliant codes, and QR codes on every invoice.", color: "#4f8ef7" },
              { icon: "💳", title: "Payment Collection",          desc: "Accept Credit Card, e-Transfer, and ACH via Stripe with auto reconciliation.", color: "#f97b6b" },
              { icon: "📊", title: "Expense Management",          desc: "Track all business expenses and generate clean financial reports.", color: "#52c97e" },
              { icon: "📁", title: "T1/T2 CRA Reports",           desc: "Generate CRA-ready income summaries and files for T1 and T2 returns.", color: "#a78bfa" },
              { icon: "👥", title: "Client & Vendor Management",  desc: "Manage customer and vendor details, track payment history effortlessly.", color: "#f97b6b" },
              { icon: "📱", title: "Mobile & Desktop",            desc: "Access billing from anywhere via iOS, Android, and web browsers.", color: "#4f8ef7" },
            ].map((item, i) => (
              <div key={i} ref={setRef(i)} className="card anim-scale-in" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="card-icon" style={{ background: item.color + "18" }}>
                  <span style={{ fontSize: 24 }}>{item.icon}</span>
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 8 }}>{item.title}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.75 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button className="btn-blue" onClick={() => navigate("/login")}>View all features</button>
          </div>
        </div>
      </section>

      {/* ═══ PAYROLL ═════════════════════════════════════════════════════════ */}
      <section id="payroll" style={{ padding: "88px 0", background: "white", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow>New Feature</Eyebrow>
            <SectionHeading>Built-in payroll management</SectionHeading>
            <SectionSub>Handle payroll, deductions, and CRA remittances without leaving InvoiceCloud.</SectionSub>
          </div>
          <div className="payroll-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {[
              { img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&q=90&fit=crop&crop=faces", title: "Employee Payroll Processing",  desc: "Run full payroll for hourly, salaried, and contract workers.", color: "#4f8ef7" },
              { img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&q=90&fit=crop",               title: "CPP & EI Auto Deductions",     desc: "Automatically calculate and apply CPP and EI contributions.", color: "#f97b6b" },
              { img: "https://images.unsplash.com/photo-1568234928966-359c35dd8327?w=400&q=90&fit=crop",            title: "T4 Slip Generation",           desc: "Generate and file T4 slips for all employees from the dashboard.", color: "#52c97e" },
              { img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=90&fit=crop",            title: "ROE Export",                   desc: "Instantly generate Record of Employment forms when needed.", color: "#a78bfa" },
              { img: "https://images.unsplash.com/photo-1601597111158-2fceff292cdc?w=400&q=90&fit=crop",            title: "Direct Deposit Integration",   desc: "Pay employees directly to their bank accounts on payday.", color: "#f97b6b" },
              { img: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=400&q=90&fit=crop",               title: "CRA Remittance Reports",       desc: "Auto-generate payroll remittance reports ready for CRA.", color: "#4f8ef7" },
            ].map((item, i) => (
              <div key={i} ref={setRef(i + 10)} className="card anim-scale-in" style={{ animationDelay: `${i * 0.08}s` }}>
                <img src={item.img} alt={item.title} style={{ width: 60, height: 60, borderRadius: 12, objectFit: "cover", marginBottom: 18, border: `2px solid ${item.color}20` }} />
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 8 }}>{item.title}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.75 }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button className="btn-blue" onClick={() => navigate("/login")}>Try payroll free</button>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <section style={{ padding: "88px 0", background: "#fafafa", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow>Simple Process</Eyebrow>
            <SectionHeading>Up and running in 4 steps</SectionHeading>
          </div>
          <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
            {[
              { n: "01", t: "Sign Up",       d: "Create your free account in 2 minutes", color: "#4f8ef7" },
              { n: "02", t: "Add Business",  d: "Enter your business and CRA Business Number", color: "#f97b6b" },
              { n: "03", t: "Add Products",  d: "Upload your products or services", color: "#52c97e" },
              { n: "04", t: "Start Billing", d: "Create your first GST/HST invoice", color: "#a78bfa" },
            ].map((item, i) => (
              <div key={i} ref={setRef(i + 20)} className="anim-scale-in" style={{
                animationDelay: `${i * 0.1}s`,
                padding: "32px 24px", textAlign: "center",
                borderRight: i < 3 ? "1px solid var(--border)" : "none",
              }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 52, fontWeight: 800, color: item.color + "30", marginBottom: 8 }}>{item.n}</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: "var(--ink)", marginBottom: 8 }}>{item.t}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#888", lineHeight: 1.6 }}>{item.d}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 44 }}>
            <button className="btn-primary" onClick={() => navigate("/login")}>
              Get started — 14-day free trial
            </button>
            <p style={{ fontFamily: "'DM Sans', sans-serif", color: "#888", fontSize: "var(--text-xs)", marginTop: 10 }}>No credit card required</p>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═════════════════════════════════════════════════════════ */}
      <section id="pricing" style={{ padding: "88px 0", background: "linear-gradient(135deg, #f0f7ff, #fff5f4)", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Eyebrow>Pricing</Eyebrow>
            <SectionHeading>Simple, transparent pricing</SectionHeading>
            <p style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink-muted)", fontSize: "var(--text-base)" }}>Start free, upgrade anytime</p>
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 48 }}>
            <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.8)", border: "1px solid var(--border)", borderRadius: 50, padding: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              {["monthly", "yearly"].map(p => (
                <button key={p} onClick={() => setBilling(p)} style={{
                  padding: "9px 24px", borderRadius: 50,
                  border: "none", cursor: "pointer",
                  background: billing === p ? "white" : "transparent",
                  color: billing === p ? "var(--ink)" : "var(--ink-muted)",
                  fontWeight: 500, fontSize: "var(--text-sm)",
                  boxShadow: billing === p ? "0 2px 8px rgba(0,0,0,.08)" : "none",
                  transition: "all .2s",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {p === "monthly" ? "Monthly" : <>Yearly <span style={{ fontSize: "var(--text-xs)", color: "#52c97e", fontWeight: 700 }}>Save 16%</span></>}
                </button>
              ))}
            </div>
          </div>

          <div className="plans-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24, alignItems: "center" }}>
            {plans.map((plan, i) => {
              const pc = planColors[plan.key];
              return (
                <div key={i} ref={setRef(i + 30)} className={`plan-card anim-scale-in${plan.popular ? " featured" : ""}`}
                  style={{ animationDelay: `${i * 0.08}s`, background: pc.bg }}>
                  {plan.popular && (
                    <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "#f97b6b", color: "white", padding: "4px 18px", borderRadius: 20, fontSize: 12, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", fontWeight: 500 }}>
                      Most Popular
                    </div>
                  )}
                  <div style={{ padding: "28px 24px", flex: 1, display: "flex", flexDirection: "column" }}>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, color: pc.color, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>{plan.name}</div>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 800, color: "var(--ink)" }}>
                      ${pricing[billing][plan.key]}
                    </div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginBottom: 20 }}>
                      CAD / {billing === "monthly" ? "mo" : "yr"}
                    </div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: pc.color, marginBottom: 16, fontWeight: 500 }}>For {plan.target}</div>
                    <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 18, marginBottom: 22, flex: 1 }}>
                      {plan.features.map((f, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <span style={{ color: pc.color, fontSize: 14 }}>✓</span>
                          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#555" }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={plan.key === "enterprise" ? () => scrollTo("contact") : () => navigate("/login")} style={{
                      width: "100%", padding: "12px", borderRadius: 50,
                      background: plan.popular ? pc.color : "transparent",
                      color: plan.popular ? "white" : pc.color,
                      border: `2px solid ${pc.color}`,
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "all 0.2s",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = pc.color; e.currentTarget.style.color = "white"; }}
                      onMouseLeave={e => { if (!plan.popular) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = pc.color; } }}
                    >
                      {plan.cta}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: "center", marginTop: 44 }}>
            <div style={{ display: "inline-block", background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "28px 36px", maxWidth: 400, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 8 }}>Need a custom enterprise plan?</h3>
              <p style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink-muted)", fontSize: "var(--text-sm)", marginBottom: 18 }}>Custom pricing for CPA firms and large enterprises</p>
              <button className="btn-secondary" style={{ fontSize: "var(--text-sm)", padding: "10px 22px" }} onClick={() => scrollTo("contact")}>Contact sales team</button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WHO USES ════════════════════════════════════════════════════════ */}
      <section id="who-uses" style={{ padding: "88px 0", background: "#fafafa", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow>Who uses InvoiceCloud?</Eyebrow>
            <SectionHeading>Trusted across Canada</SectionHeading>
            <SectionSub>CPAs, consultants, and businesses in every province rely on InvoiceCloud.</SectionSub>
          </div>

          <div className="profession-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24, marginBottom: 32 }}>
            {[
              { img: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&q=90&fit=crop&crop=faces", t: "Practicing CPA",           d: "Manage multiple client accounts, T-slips, and GST/HST returns from one dashboard.", color: "#4f8ef7" },
              { img: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=90&fit=crop",          t: "CPA Firm",                d: "Team access, bulk billing, and dedicated client dashboards for your firm.", color: "#f97b6b" },
              { img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=90&fit=crop",          t: "Tax Consultant",          d: "Quick invoicing, expense tracking, and instant CRA reports for clients.", color: "#52c97e" },
              { img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=90&fit=crop",             t: "Accountant / Bookkeeper",  d: "Simple billing, ledger management, and clean financial reports.", color: "#a78bfa" },
            ].map((item, i) => (
              <div key={i} ref={setRef(i + 40)} className="card anim-scale-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <img src={item.img} alt={item.t} style={{ width: 60, height: 60, borderRadius: 12, objectFit: "cover", marginBottom: 18, border: `2px solid ${item.color}20` }} />
                <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 7 }}>{item.t}</h4>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.75 }}>{item.d}</p>
              </div>
            ))}
          </div>

          <div className="industry-grid" style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 40 }}>
            {[
              { label: "Retail",        color: "#4f8ef7" },
              { label: "Manufacturing", color: "#f97b6b" },
              { label: "Freelancer",    color: "#52c97e" },
              { label: "Restaurant",    color: "#a78bfa" },
              { label: "E-commerce",    color: "#f97b6b" },
              { label: "Healthcare",    color: "#4f8ef7" },
            ].map((item, i) => (
              <div key={i} style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                padding: "14px 10px",
                textAlign: "center",
                cursor: "default",
                transition: "border-color .2s, background .2s, transform .2s, box-shadow .2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = item.color; e.currentTarget.style.background = item.color + "10"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 6px 16px ${item.color}22`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "white"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3 }}>{item.label}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center" }}>
            <button className="btn-blue" style={{ fontSize: "var(--text-base)", padding: "15px 34px" }} onClick={() => navigate("/login")}>
              Start free trial — no credit card required
            </button>
          </div>
        </div>
      </section>

      {/* ═══ WHY US ══════════════════════════════════════════════════════════ */}
      <section id="why-us" style={{ padding: "88px 0", background: "white", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <Eyebrow>Why InvoiceCloud?</Eyebrow>
            <SectionHeading>Built for Canadian businesses</SectionHeading>
            <SectionSub>By people who understand Canadian billing, taxes, and payroll.</SectionSub>
          </div>
          <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
            {[
              { t: "Canadian Tax First",  d: "Built for GST/HST/PST. CRA-compliant codes and tax reports you can actually understand.", color: "#4f8ef7" },
              { t: "Clean & Simple",      d: "Create an invoice in under 60 seconds — even on your phone. No clutter, no confusion.", color: "#f97b6b" },
              { t: "Secure Cloud Access", d: "Bank-level encryption keeps your data private. Accessible anytime, anywhere.", color: "#52c97e" },
              { t: "Fair Pricing in CAD", d: "Plans in Canadian dollars for every business size — freelancers to large firms.", color: "#a78bfa" },
            ].map((item, i) => (
              <div key={i} ref={setRef(i + 50)} className="anim-scale-in" style={{
                animationDelay: `${i * 0.1}s`,
                background: "white",
                border: "1px solid var(--border)",
                borderTop: `3px solid ${item.color}`,
                borderRadius: "var(--radius-lg)",
                padding: "28px 24px",
                transition: "box-shadow .25s, transform .25s",
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 12px 40px ${item.color}18`; e.currentTarget.style.transform = "translateY(-6px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}
              >
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 10 }}>{item.t}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.75 }}>{item.d}</p>
              </div>
            ))}
          </div>

          <div style={{ maxWidth: 600, margin: "52px auto 0" }}>
            <div style={{
              background: "var(--blue-light)", border: "1px solid var(--blue-mid)",
              borderRadius: "var(--radius-xl)", padding: "40px 36px", textAlign: "center",
            }}>
              <p style={{ fontFamily: "'Playfair Display', serif", color: "var(--ink-muted)", fontStyle: "italic", fontSize: "var(--text-md)", lineHeight: 1.85, marginBottom: 24 }}>
                "InvoiceCloud Canada is built for businesses who want powerful billing without the complexity. We're working closely with our first customers to refine every feature — and we'd love to have you with us."
              </p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                <div style={{ width: 42, height: 42, background: "linear-gradient(135deg, #4f8ef7, #6366f1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "var(--text-lg)", fontFamily: "'Playfair Display', serif" }}>O</div>
                <div style={{ textAlign: "left" }}>
                  <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-base)", color: "var(--ink)" }}>Omkar Joshi</p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-faint)" }}>Founder, InvoiceCloud Canada</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═════════════════════════════════════════════════════════════ */}
      <section style={{ padding: "88px 0", background: "#fafafa", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <Eyebrow>FAQ</Eyebrow>
            <SectionHeading>Common questions</SectionHeading>
          </div>
          <div className="faq-list" style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { q: "Is InvoiceCloud compliant with Canadian tax law?", a: "Yes — fully compliant with GST/HST/PST. Automatic calculations and CRA-compliant codes are built in." },
              { q: "Can I try before purchasing?",                      a: "All paid plans include a 14-day free trial. No credit card required to start." },
              { q: "What happens after the 14-day trial?",             a: "Choose any subscription plan. Your data is preserved when you upgrade." },
              { q: "Does payroll handle Quebec (QST/QPP)?",            a: "Yes — Quebec-specific deductions including QPP and QST are fully supported." },
            ].map((faq, i) => (
              <div key={i} ref={setRef(i + 60)} className="faq-card anim-scale-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 8 }}>{faq.q}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)", lineHeight: 1.75 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA BANNER ══════════════════════════════════════════════════════ */}
      <section style={{ padding: "88px 24px", background: "linear-gradient(135deg, #1a1a2e, #16213e)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-60%", left: "50%", transform: "translateX(-50%)", width: 600, height: 600, background: "radial-gradient(circle, rgba(79,142,247,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(30px,5vw,46px)", fontWeight: 800, color: "white", marginBottom: 16, letterSpacing: "-1px", lineHeight: 1.1 }}>
            Ready to transform<br />your billing?
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", color: "rgba(255,255,255,.6)", fontSize: "var(--text-md)", marginBottom: 36, lineHeight: 1.7 }}>
            Start your 14-day free trial today. No credit card required.
          </p>
          <div className="cta-btns" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/login")} style={{
              background: "white", color: "#4f8ef7", border: "none",
              borderRadius: 50, padding: "15px 36px",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600, fontSize: "var(--text-base)", cursor: "pointer",
              transition: "transform .15s, box-shadow .2s",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)"; }}
            >
              Start 14-day free trial
            </button>
            <button onClick={() => alert("Demo Request Received!\nOur team will contact you.")} style={{
              background: "transparent", color: "white",
              border: "2px solid rgba(255,255,255,0.4)",
              borderRadius: 50, padding: "15px 36px",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500, fontSize: "var(--text-base)", cursor: "pointer",
              transition: "border-color .2s, background .2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)"; e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.background = "transparent"; }}
            >
              Schedule a demo
            </button>
          </div>
        </div>
      </section>

      {/* ═══ CONTACT ═════════════════════════════════════════════════════════ */}
      <section id="contact" style={{ padding: "88px 0", background: "white", borderTop: "1px solid var(--border)" }}>
        <div className="section-wrap">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow>Contact</Eyebrow>
            <SectionHeading>Get in touch</SectionHeading>
            <p style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink-muted)", fontSize: "var(--text-base)" }}>Have questions? We respond within 24 hours.</p>
          </div>

          {submitStatus.message && (
            <div style={{
              maxWidth: 680, margin: "0 auto 32px",
              padding: "14px 18px", borderRadius: "var(--radius-md)",
              background: submitStatus.type === "success" ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${submitStatus.type === "success" ? "#bbf7d0" : "#fecaca"}`,
              color: submitStatus.type === "success" ? "#166534" : "#991b1b",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "var(--text-sm)", display: "flex", alignItems: "center", gap: 10, fontWeight: 500,
            }}>
              <span>{submitStatus.type === "success" ? "✅" : "❌"}</span>
              {submitStatus.message}
            </div>
          )}

          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
            <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "36px 32px", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "var(--text-xl)", color: "var(--ink)", marginBottom: 24 }}>Send us a message</h3>
              <form onSubmit={submitForm} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { p: "Your Name",      n: "name",  t: "text"  },
                  { p: "Email Address",  n: "email", t: "email" },
                  { p: "Phone Number",   n: "phone", t: "text"  },
                ].map(f => (
                  <input key={f.n} name={f.n} type={f.t} placeholder={f.p} required disabled={isSubmitting} style={{
                    padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "var(--text-base)", color: "var(--ink)", background: "var(--surface)", transition: "all .2s",
                  }} />
                ))}
                <select name="plan" disabled={isSubmitting} style={{
                  padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "var(--text-base)", color: "var(--ink-muted)", background: "var(--surface)",
                }}>
                  <option value="">Select plan interest</option>
                  {["basic", "professional", "business", "enterprise"].map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
                <textarea name="message" placeholder="Your message" disabled={isSubmitting} style={{
                  padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "var(--text-base)", color: "var(--ink)", height: 110, resize: "none",
                  background: "var(--surface)", transition: "all .2s",
                }} />
                <button type="submit" disabled={isSubmitting} className="btn-blue" style={{ width: "100%", justifyContent: "center", fontSize: "var(--text-base)", padding: "14px 0" }}>
                  {isSubmitting ? (
                    <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} /> Sending...</>
                  ) : "Send message"}
                </button>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", color: "var(--ink-faint)", textAlign: "center" }}>We'll respond within 24 hours</p>
              </form>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", height: 160, border: "1px solid var(--border)" }}>
                <img
                  src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=90&fit=crop&crop=center"
                  alt="Toronto Canada"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", padding: "32px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "var(--text-lg)", color: "var(--ink)", marginBottom: 24 }}>Contact information</h3>
                {[
                  { label: "Office", lines: ["InvoiceCloud Canada Inc.", "Toronto, Ontario, Canada"], color: "#4f8ef7" },
                  { label: "Phone",  lines: ["+1 (416) 000-0000", "Mon–Fri, 9AM–6PM EST"], color: "#52c97e" },
                  { label: "Email",  lines: ["billing@invoicecloud.ca", "We respond within 24 hours"], color: "#f97b6b" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, marginBottom: i < 2 ? 20 : 0, alignItems: "flex-start" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, marginTop: 6, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--ink)", marginBottom: 2 }}>{item.label}</p>
                      {item.lines.map((l, j) => <p key={j} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)" }}>{l}</p>)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)", borderRadius: "var(--radius-xl)", padding: "24px 22px" }}>
                <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "var(--ink)", marginBottom: 14, fontSize: "var(--text-base)" }}>What happens after you contact us?</h4>
                {["You'll receive a confirmation email immediately", "Our team reviews your inquiry within 2 hours", "We'll contact you via phone or email within 24 hours"].map((t, i) => (
                  <CheckItem key={i} green>{t}</CheckItem>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ══════════════════════════════════════════════════════════ */}
      <footer style={{ background: "#0f0f1a" }}>
        <div className="footer-grid" style={{
          maxWidth: 1200, margin: "0 auto",
          padding: "60px 48px 48px",
          display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48,
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 16 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <img src={LOGO_SRC} alt="InvoiceCloud" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain", border: "1px solid #1e1e2e" }} onError={e => e.currentTarget.style.display = "none"} />
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: "white", lineHeight: 1 }}>
                  Invoice<span style={{ color: "#4f8ef7" }}>Cloud</span>
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", fontWeight: 600, letterSpacing: "1.4px", color: "#4f8ef7", textTransform: "uppercase" }}>Canada</div>
              </div>
            </div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "#666", lineHeight: 1.75, maxWidth: 240 }}>Premium billing and payroll solution for Canadian businesses, freelancers, and CPAs.</p>
          </div>

          {[
            { title: "Company", items: [
              { l: "Features",  fn: () => scrollTo("features") },
              { l: "Payroll",   fn: () => scrollTo("payroll")  },
              { l: "Pricing",   fn: () => scrollTo("pricing")  },
              { l: "Why Us",    fn: () => scrollTo("why-us")   },
              { l: "Contact",   fn: () => scrollTo("contact")  },
            ]},
            { title: "Product", items: [
              { l: "GST/HST Invoicing",   fn: () => scrollTo("features") },
              { l: "Payroll Management",  fn: () => scrollTo("payroll")  },
              { l: "CRA Reports",         fn: () => scrollTo("features") },
              { l: "Start Free Trial",    fn: () => navigate("/login")   },
            ]},
            { title: "Contact", items: [
              { l: "+1 (416) 000-0000" },
              { l: "billing@invoicecloud.ca" },
              { l: "Toronto, Ontario" },
            ]},
          ].map((col, i) => (
            <div key={i}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", fontWeight: 600, color: "#555", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 16 }}>{col.title}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {col.items.map((item, j) => (
                  <li key={j} style={{ marginBottom: 10 }}>
                    {item.fn
                      ? <button className="footer-link" onClick={item.fn} style={{ background: "none", border: "none", color: "#666", fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", cursor: "pointer", padding: 0, transition: "color 0.2s" }}
                          onMouseEnter={e => e.target.style.color = "#4f8ef7"}
                          onMouseLeave={e => e.target.style.color = "#666"}
                        >{item.l}</button>
                      : <span style={{ fontFamily: "'DM Sans', sans-serif", color: "#666", fontSize: "var(--text-sm)" }}>{item.l}</span>
                    }
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ borderTop: "1px solid #1e1e2e" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 48px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", color: "#444" }}>© 2026 InvoiceCloud Canada Inc. All rights reserved.</p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-xs)", color: "#444" }}>Made in Canada 🍁 · <a href="mailto:billing@invoicecloud.ca" style={{ color: "#4f8ef7" }}>billing@invoicecloud.ca</a></p>
          </div>
          <div style={{ borderTop: "1px solid #1e1e2e", padding: "12px 24px", textAlign: "center", display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            {legalLinks.map((link, i) => (
              <React.Fragment key={link.key}>
                <button className="policy-link" onClick={() => openPolicy(link.key)} style={{
                  background: "none", border: "none", color: "#444",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "var(--text-xs)", cursor: "pointer", padding: "4px 6px", transition: "color .2s",
                }}>{link.label}</button>
                {i < legalLinks.length - 1 && <span style={{ color: "#2a2a3e", fontSize: "var(--text-xs)" }}>|</span>}
              </React.Fragment>
            ))}
          </div>
          <div style={{ textAlign: "center", paddingBottom: 8 }}>
            <span onClick={() => navigate("/superadmin")} style={{ color: "transparent", cursor: "pointer", userSelect: "none", fontSize: 10, display: "inline-block", padding: "4px 8px" }}>pournima</span>
          </div>
        </div>
      </footer>
    </div>
  );
}